import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { Delete, CloudUpload } from '@mui/icons-material';
import {
  uploadCatalogImage,
  SB_ICON_FOLDER,
  SB_BANNER_FOLDER,
} from '../../../services/serviceBooking';

interface SingleProps {
  label: string;
  value?: string;
  folder?: number;
  onChange: (url: string) => void;
  onError?: (msg: string) => void;
}

/** Single-image picker (the `icon` field). Uploads to S3 immediately. */
export const ImagePicker: React.FC<SingleProps> = ({
  label,
  value,
  folder = SB_ICON_FOLDER,
  onChange,
  onError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setBusy(true);
      const url = await uploadCatalogImage(file, folder);
      onChange(url);
    } catch (err: any) {
      onError?.(err?.message || 'Image upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        {value ? (
          <Box sx={{ position: 'relative', width: 64, height: 64 }}>
            <img
              src={value}
              alt={label}
              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #e0e0e0' }}
            />
            <IconButton
              size="small"
              onClick={() => onChange('')}
              sx={{ position: 'absolute', top: -10, right: -10, bgcolor: '#fff', boxShadow: 1 }}
            >
              <Delete fontSize="small" color="error" />
            </IconButton>
          </Box>
        ) : (
          <Button
            variant="outlined"
            size="small"
            startIcon={busy ? <CircularProgress size={16} /> : <CloudUpload />}
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        )}
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} />
      </Box>
    </Box>
  );
};

interface GalleryProps {
  label: string;
  value?: string[];
  folder?: number;
  onChange: (urls: string[]) => void;
  onError?: (msg: string) => void;
}

/** Multi-image gallery (the `images` field). */
export const GalleryPicker: React.FC<GalleryProps> = ({
  label,
  value = [],
  folder = SB_BANNER_FOLDER,
  onChange,
  onError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      setBusy(true);
      const urls = await Promise.all(files.map((f) => uploadCatalogImage(f, folder)));
      onChange([...value, ...urls]);
    } catch (err: any) {
      onError?.(err?.message || 'Image upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
        {value.map((url, idx) => (
          <Box key={url + idx} sx={{ position: 'relative', width: 64, height: 64 }}>
            <img
              src={url}
              alt={`${label}-${idx}`}
              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #e0e0e0' }}
            />
            <IconButton
              size="small"
              onClick={() => removeAt(idx)}
              sx={{ position: 'absolute', top: -10, right: -10, bgcolor: '#fff', boxShadow: 1 }}
            >
              <Delete fontSize="small" color="error" />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="outlined"
          size="small"
          startIcon={busy ? <CircularProgress size={16} /> : <CloudUpload />}
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          sx={{ height: 64 }}
        >
          {busy ? 'Uploading…' : 'Add'}
        </Button>
        <input ref={inputRef} type="file" accept="image/*" hidden multiple onChange={handleFiles} />
      </Box>
    </Box>
  );
};
